import { useSearchBiblesMutation } from '@soulhx/client-store';
import { type Bible, isValidBibleChooserData, type BibleChooserData } from '@soulhx/fs-common';
import { PlainTextEdit, StandardButton } from '@soulhx/ui-common';
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Col, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

interface ISpecificVersion {
  chooserData: BibleChooserData;
  update: (newData: BibleChooserData) => void;
  onlyOne: boolean;
  remove: () => void;
}
export const SpecificVersion = ({ chooserData, update, onlyOne, remove }: ISpecificVersion) => {
  const isValid = useMemo(() => {
    return isValidBibleChooserData({ inputArray: [chooserData] });
  }, [chooserData]);
  const [isEditing, setIsEditing] = useState(onlyOne || !isValid);
  const [editedAbbrev, setEditedAbbrev] = useState(chooserData.abbreviationLocal);
  const [results, setResults] = useState<Bible[]>([]);
  const [searchApi] = useSearchBiblesMutation();
  const { t } = useTranslation(['bibleviewer']);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    searchApi(editedAbbrev)
      .unwrap()
      .then((results) => {
        setResults(results);
      })
      .catch(() => setIsError(true));
  }, [searchApi, editedAbbrev, setResults]);

  if (isError) {
    return <div className="bibleviewer-content-container-error">{t('bibleviewer:errors.getBiblesError')}</div>;
  }

  return (
    <Row className="m-0 mt-2 p-0 w-100">
      <Col xs="10">
        <PlainTextEdit
          isReadOnly={!isEditing}
          value={editedAbbrev}
          onFocus={() => setIsEditing(true)}
          onChange={(e) => {
            setEditedAbbrev(e.currentTarget.value);
          }}
          placeholder={t('bibleviewer:versionSearch.blank')}
          isInvalid={!isValid}
        />
      </Col>
      <Col xs="2">
        <StandardButton type="delete" onClick={() => remove()} />
      </Col>

      {isEditing && results.length > 0 && (
        <Col xs="12">
          <ListGroup className="mt-4 w-100">
            {results.map((result) => (
              <ListGroup.Item
                action
                className="d-flex justify-content-between align-items-start"
                key={`bible-${result.id}`}
                onClick={() => {
                  setEditedAbbrev(result.abbreviationLocal);
                  update({
                    abbreviation: result.abbreviation,
                    abbreviationLocal: result.abbreviationLocal,
                    bibleId: result.id,
                  });
                  setIsEditing(false);
                }}
              >
                <div>
                  <div>{result.nameLocal}</div>
                  <div>{result.descriptionLocal}</div>
                </div>
                <Badge pill>{result.language.nameLocal}</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Col>
      )}
    </Row>
  );
};
